import { useEffect, useState } from "react";
import { Slider } from "antd";
import { ForwardOutlined, BackwardOutlined } from "@ant-design/icons";
import moment from "moment-timezone";

import playButton from "../../../assets/icons/play.png";
import pauseButton from "../../../assets/icons/pauseIcon.png";
import forwardButton from "../../../assets/icons/forward15.png";
import backwardButton from "../../../assets/icons/backward15.png";
import downArrow from "../../../assets/icons/downArrow.png";
import upArrow from "../../../assets/icons/upArrow.png";
import { useSelector } from "react-redux";

const TimeMachineV2Controller = ({
  isPlaying,
  setIsPlaying,
  forwarded15Seconds,
  backward15Seconds,
  timeMachineLoader,
  totalSeekPosition,
  currentSeekPosition,
  handleOnChange,
  timeFrame,
  isPlayingForward,
  setIsPlayingForward,
  playbackRate,
  setPlaybackRate
}) => {
  const [timeMachineControllerIsOpen, setTimeMachineControllerIsOpen] =
    useState(true);
  const [endTime, setEndTime] = useState("");
  const [tempSeekPosition, setTempSeekPosition] = useState(currentSeekPosition);
  const [isDragging, setIsDragging] = useState(false);

  const { plantTimeZone } = useSelector((state) => state.plant.plantDetails);

  const speedLevels = [1, 2, 3, 5];

  const playPauseTimeMachine = () => {
    setIsPlaying(!isPlaying);
  };

  const convertToUserTime = (timeString) => {
    if (!timeString) return "";
    let [date, time] = timeString.split("T");
    time = time.split(":");
    time = `${time[0]}:${time[1]}:${time[2].split(".")[0]}`;

    const timeOfPlant = moment.tz(`${date} ${time}`, plantTimeZone);
    return timeOfPlant
      .clone()
      .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
      .format("YYYY-MM-DD HH:mm:ss");
  };

  const getTimeBasedOnUserTime = (currentSeekPosition) => {
    if (timeFrame.length) {
      return convertToUserTime(timeFrame[currentSeekPosition]);
    }
    return "";
  };

  const handleSliderChange = (value) => {
    setIsDragging(true);
    setTempSeekPosition(value);
  };

  const handleOnComplete = (value) => {
    setIsDragging(false);
    handleOnChange(value);
  };

  const isFastForwarOrReverseModeActive = (forward = true) => {
    return forward
      ? playbackRate > 1 && isPlayingForward
      : playbackRate > 1 && !isPlayingForward;
  };

  const toggleSpeed = (isInForwardDirection = true) => {
    setIsPlaying(true);
    if (isPlayingForward !== isInForwardDirection) {
      if (playbackRate !== 1) {
        // Change direction and reset to default speed
        setPlaybackRate(1);
        setIsPlayingForward(true);
        return;
      } else {
        // From default speed, go to 2x in opposite direction
        setPlaybackRate(2);
      }
      setIsPlayingForward(isInForwardDirection);
      return;
    }

    const currentIndex = speedLevels.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speedLevels.length;
    if (nextIndex === 0) {
      setIsPlayingForward(true);
    }
    setPlaybackRate(speedLevels[nextIndex]);
  };

  useEffect(() => {
    if (!isDragging) {
      setTempSeekPosition(currentSeekPosition);
    }
  }, [currentSeekPosition, isDragging]);

  useEffect(() => {
    if (timeFrame.length) {
      setEndTime(convertToUserTime(timeFrame[timeFrame.length - 1]));
    }
  }, [timeFrame]);

  return (
    <>
      {timeMachineControllerIsOpen ? (
        <div className="time-machine-controller-container">
          <div className="time-machine-sections">
            <div className="time-machine-controller-top-section">
              <Slider
                className="time-machine-seek-bar"
                name="timeMachineSeekbar"
                id="timeMachineSeekbarId"
                min={0}
                max={totalSeekPosition}
                value={tempSeekPosition}
                onChange={handleSliderChange}
                onChangeComplete={handleOnComplete}
                draggableTrack={false}
                controlSize={20}
                tooltip={{ formatter: getTimeBasedOnUserTime }}
                railSize={200}
                dotActiveBorderColor="#4461F2"
                dotBorderColor="#4461F2"
                handleActiveColor="#EEEEEE"
                handleColor="#EEEEEE"
                trackBg="#EEEEEE"
                trackHoverBg="#EEEEEE"
              />
            </div>
            <div className="time-machine-controller-bottom-section">
              <div className="time-bar-section">
                {/* <span>{currentTimeStamp} -</span> */}
                {`${getTimeBasedOnUserTime(currentSeekPosition)} / ${endTime} `}
              </div>
              <div className="control-buttons-section">
                <button
                  onClick={() => toggleSpeed(false)}
                  className={`fast-reverse ${
                    isFastForwarOrReverseModeActive(false) ? "active" : ""
                  }`}
                >
                  <BackwardOutlined />
                  <span className="playback-rate">
                    <span key={playbackRate} className="slide-up-text">
                      {playbackRate}x
                    </span>
                  </span>
                </button>

                <img
                  onClick={backward15Seconds}
                  className={`${
                    timeMachineLoader || "time-machine-fast-forward-btn"
                  }`}
                  src={backwardButton}
                />
                {isPlaying ? (
                  <img
                    src={pauseButton}
                    onClick={playPauseTimeMachine}
                    className={`${
                      timeMachineLoader
                        ? "no-action-allowed"
                        : "time-machine-play-btn"
                    }`}
                  />
                ) : (
                  <img
                    onClick={playPauseTimeMachine}
                    className={`${
                      timeMachineLoader
                        ? "no-action-allowed"
                        : "time-machine-play-btn"
                    }`}
                    src={playButton}
                  />
                )}
                <img
                  onClick={forwarded15Seconds}
                  className={`${
                    timeMachineLoader || "time-machine-fast-forward-btn"
                  }`}
                  src={forwardButton}
                />
                <button
                  onClick={() => toggleSpeed(true)}
                  className={`fast-forward ${
                    isFastForwarOrReverseModeActive(true) ? "active" : ""
                  }`}
                >
                  <ForwardOutlined />
                  <span className="playback-rate">
                    <span key={playbackRate} className="slide-up-text">
                      {playbackRate}x
                    </span>
                  </span>
                </button>
              </div>
              <div
                onClick={() =>
                  setTimeMachineControllerIsOpen(!timeMachineControllerIsOpen)
                }
                className="toggle-controller-section"
              >
                <h4>Hide Controls</h4>
                <img src={downArrow} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="small-controller-toggle-container">
          <div
            onClick={() =>
              setTimeMachineControllerIsOpen(!timeMachineControllerIsOpen)
            }
            className="small-controller-toggle-section"
          >
            <img src={upArrow} />
            <h4>Open Controls</h4>
          </div>
        </div>
      )}
    </>
  );
};

export default TimeMachineV2Controller;
